
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { ClothingItem } from "../types";
import { useAuth } from "./AuthContext";
import { useAdminViewAs } from "./AdminViewAsContext";
import { 
  fetchUserClothingItems, 
  addClothingItem, 
  updateClothingItem,
  deleteClothingItem,
  toggleItemFavorite
} from "../services/clothingService";
import { isDigitalClosetUnlocked } from "../utils/pagePermissions";
import { initialItems } from "../constants/initialClothingItems";
import { toast } from "sonner";

/**
 * 'self-only': the customer may see only pieces they added themselves
 * (uploaded_by === user_id) until page_permissions.digital_closet is granted
 * -- auto at stage closet_ready, or by an admin. 'loading' fails closed.
 */
export type ClosetAccess = 'loading' | 'full' | 'self-only';

interface ClothingContextType {
  /**
   * The viewed closet. In the customer's OWN session this is already
   * restricted per `closetAccess`; admin view-as and shared sessions get the
   * full list so staff tooling keeps working (Dashboard1 mirrors the
   * restriction for view-as itself).
   */
  items: ClothingItem[];
  addItem: (item: Omit<ClothingItem, "id">) => void;
  updateItem: (id: string, updates: Partial<ClothingItem>) => Promise<boolean>;
  getItemById: (id: string) => ClothingItem | undefined;
  deleteItem: (id: string) => Promise<boolean>;
  toggleFavorite: (id: string) => Promise<boolean>;
  isLoading: boolean;
  refreshItems: () => Promise<void>;
  isSharedCloset: boolean;
  /** Permission state of the VIEWED user's closet (own session or view-as). */
  closetAccess: ClosetAccess;
  /** Add/Edit controls only make sense in the customer's own session: addItem
   *  and AddItemForm write as user.id, so in view-as they'd hit the admin's closet. */
  canAddItems: boolean;
}

const ClothingContext = createContext<ClothingContextType | undefined>(undefined);

export const ClothingProvider = ({ children }: { children: ReactNode }) => {
  const [rawItems, setRawItems] = useState<ClothingItem[]>([]);
  const [closetAccess, setClosetAccess] = useState<ClosetAccess>('loading');
  const [isLoading, setIsLoading] = useState(true);
  const { user, sharedAccess } = useAuth();
  const { viewAsUser, isAdminViewMode } = useAdminViewAs();
  
  // Priority order: admin view-as > shared access > logged-in user
  const userId = viewAsUser?.id || sharedAccess?.userId || user?.id;
  
  // Determine if we're in shared closet mode (includes admin view mode)
  const isSharedCloset = !!sharedAccess || isAdminViewMode;

  const loadItems = useCallback(async (isCancelled: () => boolean = () => false) => {
    setIsLoading(true);

    try {
      if (userId) {
        const userItems = await fetchUserClothingItems(userId);
        // Guard against a client/user switch (admin view-as, shared closet)
        // resolving out of order and landing the wrong user's closet.
        if (isCancelled()) return;
        setRawItems(userItems);
      } else {
        try {
          const savedItems = localStorage.getItem("clothingItems");
          if (savedItems) {
            const parsedItems = JSON.parse(savedItems);
            // For items loaded from localStorage, images might be missing, that's expected
            setRawItems(parsedItems);
          } else {
            setRawItems(initialItems);
          }
        } catch (error) {
          console.error('Error parsing localStorage data:', error);
          // Clear corrupted data and use initial items
          localStorage.removeItem("clothingItems");
          setRawItems(initialItems);
        }
      }
    } catch (error) {
      console.error('Error loading clothing items:', error);
      Sentry.captureException(error, { tags: { context: 'load_clothing_items' } });
      if (isCancelled()) return;

      try {
        const savedItems = localStorage.getItem("clothingItems");
        if (savedItems) {
          const parsedItems = JSON.parse(savedItems);
          setRawItems(parsedItems);
        } else {
          setRawItems(initialItems);
        }
      } catch (storageError) {
        console.error('Error parsing localStorage data:', storageError);
        // Clear corrupted data and use initial items
        localStorage.removeItem("clothingItems");
        setRawItems(initialItems);
      }
    } finally {
      // A superseded load must not clear the loading state the fresh load just set.
      if (!isCancelled()) setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Ignore an in-flight load if userId changes before it resolves, so the
    // previous user's items can't overwrite the current user's.
    let cancelled = false;
    loadItems(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [userId]); // Re-fetch when userId changes (e.g., when switching between own and shared closet)

  // Own session = the customer looking at their own closet. Admin view-as and
  // shared closets are deliberately NOT filtered.
  const ownSession = !!user && !sharedAccess && !isAdminViewMode;

  useEffect(() => {
    // Read the VIEWED user's permission in every session, so view-as can
    // mirror what the customer sees. Starts as 'loading' and fails closed --
    // no flash of the full closet before the answer arrives.
    let cancelled = false;
    if (!userId) {
      setClosetAccess('full');
      return;
    }
    setClosetAccess('loading');
    isDigitalClosetUnlocked(userId).then((unlocked) => {
      if (!cancelled) setClosetAccess(unlocked ? 'full' : 'self-only');
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // The single enforcement point for "only what you added, until permitted".
  // Applied on read so addItem's optimistic append -- which carries the
  // trigger-stamped uploaded_by via .insert().select() -- stays visible.
  const items = useMemo(() => {
    if (!ownSession || closetAccess === 'full') return rawItems;
    return rawItems.filter((item) => item.uploaded_by === user?.id);
  }, [rawItems, ownSession, closetAccess, user?.id]);

  const refreshItems = useCallback(async () => {
    if (userId) {
      try {
        await loadItems();
        return;
      } catch (error) {
        console.error("Context: Error refreshing items", error);
        throw error;
      }
    }
  }, [userId, loadItems]);

  // Items added via /add-item are enhanced/tagged by a background worker
  // (item-processing-worker) — while any are still mid-flight, poll so the
  // "Processing…" badge clears without the customer having to refresh.
  const hasProcessingItems = useMemo(
    () => items.some((item) => item.processing_status === "processing"),
    [items]
  );

  useEffect(() => {
    if (!hasProcessingItems) return;
    const interval = setInterval(() => {
      refreshItems().catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [hasProcessingItems, refreshItems]);

  useEffect(() => {
    if (!userId && rawItems.length > 0) {
      try {
        // For unauthenticated users, store only essential data without images to avoid quota issues
        const essentialData = rawItems.map(item => ({
          ...item,
          images: [] // Remove images to reduce storage size
        }));
        localStorage.setItem("clothingItems", JSON.stringify(essentialData));
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded, clearing existing data');
          // Clear existing data and try storing just the current session's items
          localStorage.removeItem("clothingItems");
          try {
            // Try storing just the last 50 items without images
            const recentItems = rawItems.slice(-50).map(item => ({
              ...item,
              images: []
            }));
            localStorage.setItem("clothingItems", JSON.stringify(recentItems));
          } catch {
            console.warn('Unable to store items in localStorage due to quota limits');
            // Continue without localStorage storage
          }
        } else {
          console.error('Error storing items in localStorage:', error);
        }
      }
    }
  }, [rawItems, userId]);

  const toggleFavorite = useCallback(async (id: string): Promise<boolean> => {
    try {
      const item = getItemById(id);
      if (!item) return false;
      
      const newFavoriteStatus = !item.favorite;
      
      if (user && !sharedAccess) {
        // Update in database only if we're not in shared access mode
        const success = await toggleItemFavorite(id, newFavoriteStatus, user.id);
        
        if (success) {
          // Update local state only after database update is successful
          setRawItems((prevItems) =>
            prevItems.map((prevItem) =>
              prevItem.id === id ? { ...prevItem, favorite: newFavoriteStatus } : prevItem
            )
          );
          
          toast.success(newFavoriteStatus ? "Added to favorites" : "Removed from favorites");
          return true;
        }
        
        toast.error("Failed to update favorite status");
        return false;
      } else {
        // For non-authenticated users, just update local state
        setRawItems((prevItems) =>
          prevItems.map((prevItem) =>
            prevItem.id === id ? { ...prevItem, favorite: newFavoriteStatus } : prevItem
          )
        );
        
        if (sharedAccess) {
          toast.info("Favorite status changes aren't saved in shared view");
        } else {
          toast.success(newFavoriteStatus ? "Added to favorites" : "Removed from favorites");
        }
        return true;
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast.error("Failed to update favorite status");
      return false;
    }
  }, [items, user, sharedAccess]);

  const addItem = useCallback(async (item: Omit<ClothingItem, "id">) => {
    try {
      if (user && !sharedAccess) {
        const newItem = await addClothingItem(item, user.id);
        setRawItems((prevItems) => [...prevItems, newItem]);
      } else {
        const newItem = {
          ...item,
          id: crypto.randomUUID(),
        };
        setRawItems((prevItems) => [...prevItems, newItem]);
      }
    } catch (error) {
      // Re-throw so callers can gate their success UI (toasts, "added" state,
      // navigation) on the write actually succeeding. The service layer
      // (addClothingItem) has already surfaced its own error toast.
      console.error('Error adding item:', error);
      throw error;
    }
  }, [user, sharedAccess]);

  const updateItem = useCallback(async (id: string, updates: Partial<ClothingItem>): Promise<boolean> => {
    try {
      if (user && !sharedAccess) {
        
        // Call the service function to update the item in the database
        const success = await updateClothingItem(id, updates, user.id);
        
        if (success) {
          // Update local state to reflect the changes
          setRawItems((prevItems) =>
            prevItems.map((item) =>
              item.id === id ? { ...item, ...updates } : item
            )
          );
          
          
          // If admin is editing another user's item, refresh the entire items list
          const isAdminEditingOtherUser = rawItems.find(item => item.id === id)?.user_id !== user.id;
          if (isAdminEditingOtherUser) {
            await refreshItems();
          }
          
          return true;
        } else {
          return false;
        }
      } else if (sharedAccess) {
        toast.info("Cannot update items in shared view");
        return false;
      } else {
        // For non-authenticated users, just update local state
        setRawItems((prevItems) =>
          prevItems.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          )
        );
        return true;
      }
    } catch (error) {
      console.error('Error in context updateItem:', error);
      return false;
    }
  }, [user, sharedAccess, rawItems, refreshItems]);

  const getItemById = useCallback((id: string) => {
    return items.find((item) => item.id === id);
  }, [items]);

  const deleteItem = useCallback(async (id: string): Promise<boolean> => {
    try {
      if (user && !sharedAccess) {
        const success = await deleteClothingItem(id, user.id);
        
        if (success) {
          setRawItems((prevItems) => prevItems.filter((item) => item.id !== id));
          return true;
        } else {
          toast.error("Failed to delete item");
          return false;
        }
      } else if (sharedAccess) {
        toast.info("Cannot delete items in shared view");
        return false;
      } else {
        setRawItems((prevItems) => prevItems.filter((item) => item.id !== id));
        return true;
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error("Failed to delete item");
      return false;
    }
  }, [user, sharedAccess]);

  const value = useMemo(() => ({
    items,
    addItem,
    updateItem,
    getItemById,
    deleteItem,
    toggleFavorite,
    // Fail closed: while the permission is unresolved, the customer's own
    // session reports loading rather than briefly showing everything.
    isLoading: isLoading || (ownSession && closetAccess === 'loading'),
    refreshItems,
    isSharedCloset,
    closetAccess,
    canAddItems: ownSession
  }), [items, addItem, updateItem, getItemById, deleteItem, toggleFavorite, isLoading, refreshItems, isSharedCloset, closetAccess, ownSession]);

  return (
    <ClothingContext.Provider value={value}>
      {children}
    </ClothingContext.Provider>
  );
};

export const useClothing = () => {
  const context = useContext(ClothingContext);
  if (context === undefined) {
    throw new Error("useClothing must be used within a ClothingProvider");
  }
  return context;
};
